/* SmartJacket withdrawal UI. No Shopify order/payment mutation; receipt comes from the configured service. */
(() => {
  if (customElements.get('smartjacket-withdrawal')) return;
  class SmartJacketWithdrawal extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.form = this.querySelector('[data-form]');
      this.fields = this.querySelector('[data-fields]');
      this.review = this.querySelector('[data-review]');
      this.receiptPanel = this.querySelector('[data-receipt]');
      this.error = this.querySelector('[data-error]');
      this.status = this.querySelector('[data-status]');
      this.confirm = this.querySelector('[data-confirm]');
      this.edit = this.querySelector('[data-edit]');
      this.form.addEventListener('submit', event => { event.preventDefault(); this.prepareReview(); });
      this.form.addEventListener('change', () => this.updateScope());
      this.confirm.addEventListener('click', () => this.submitWithdrawal());
      this.edit.addEventListener('click', () => {
        if (this.busy) return;
        this.showStep('details');
        this.clearError();
        this.focusStep(this.fields.querySelector('legend'));
      });
      this.querySelector('[data-download]').addEventListener('click', () => this.downloadReceipt());
      try {
        const endpoint = new URL(this.dataset.endpoint);
        if (endpoint.protocol !== 'https:' || endpoint.username || endpoint.password || endpoint.hash || endpoint.search) throw new Error('configuration');
        this.endpoint = endpoint.href;
        this.fields.disabled = false;
        this.querySelector('[data-unavailable]').hidden = true;
        this.updateScope();
      } catch (_) {
        this.fields.disabled = true;
      }
    }
    updateScope() {
      const partial = this.form.elements.scope.value === 'partial';
      this.querySelector('[data-items-field]').hidden = !partial;
      this.form.elements.items.disabled = !partial;
      this.form.elements.items.required = partial;
    }
    clearError() {
      this.error.hidden = true;
      this.error.textContent = '';
      this.status.textContent = '';
    }
    showStep(step) {
      this.form.hidden = step !== 'details';
      this.review.hidden = step !== 'review';
      this.receiptPanel.hidden = step !== 'receipt';
      this.querySelectorAll('[data-step]').forEach(item => {
        if (item.dataset.step === step) item.setAttribute('aria-current', 'step');
        else item.removeAttribute('aria-current');
      });
    }
    focusStep(heading) {
      heading.focus({preventScroll:true});
      heading.scrollIntoView({block:'start', behavior:'instant'});
    }
    declaration(data) {
      return data.scope === 'partial'
        ? `Hiermit widerrufe ich den mit VARENIQ Onlinehandel UG (haftungsbeschränkt) geschlossenen Kaufvertrag zu meiner Bestellung „${data.order}“ hinsichtlich der folgenden Artikel und Mengen:\n${data.items}`
        : `Hiermit widerrufe ich den mit VARENIQ Onlinehandel UG (haftungsbeschränkt) geschlossenen Kaufvertrag zu meiner Bestellung „${data.order}“ vollständig.`;
    }
    async retryIdentity(data) {
      const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(JSON.stringify(data)));
      const hash = Array.from(new Uint8Array(bytes), b => b.toString(16).padStart(2, '0')).join('');
      const now = Date.now();
      let records = [];
      try {
        const saved = JSON.parse(sessionStorage.getItem('sj-withdrawal-retries') || '[]');
        if (Array.isArray(saved)) records = saved.filter(r => typeof r.hash === 'string' && typeof r.id === 'string' && now - r.created < 86400000).slice(-10);
      } catch (_) { /* The form also works when browser storage is blocked. */ }
      let record = records.find(r => r.hash === hash);
      if (!record) {
        record = {hash, id:crypto.randomUUID(), created:now};
        records.push(record);
      }
      try { sessionStorage.setItem('sj-withdrawal-retries', JSON.stringify(records.slice(-10))); } catch (_) {}
      return record.id;
    }
    async prepareReview() {
      if (!this.endpoint || this.busy || this.preparing || !this.form.reportValidity()) return;
      this.clearError();
      const data = Object.fromEntries(new FormData(this.form));
      for (const key of ['name', 'email', 'order', 'items']) data[key] = String(data[key] || '').trim();
      if (data.scope !== 'partial') data.items = '';
      for (const key of ['name', 'email', 'order', ...(data.scope === 'partial' ? ['items'] : [])]) {
        if (!data[key]) {
          this.form.elements[key].setCustomValidity('Bitte fülle dieses Feld aus.');
          this.form.elements[key].reportValidity();
          this.form.elements[key].addEventListener('input', () => this.form.elements[key].setCustomValidity(''), {once:true});
          return;
        }
      }
      const fingerprint = JSON.stringify(data);
      if (this.fingerprint !== fingerprint) {
        this.preparing = true;
        try {
          this.submission = {...data, requestId: await this.retryIdentity(data)};
          this.fingerprint = fingerprint;
        } finally { this.preparing = false; }
      }
      this.querySelector('[data-declaration]').textContent = this.declaration(this.submission);
      for (const key of ['name', 'email', 'order', 'items']) this.querySelector(`[data-summary="${key}"]`).textContent = this.submission[key];
      this.querySelector('[data-summary="scope"]').textContent = data.scope === 'partial' ? 'Einzelne Artikel' : 'Gesamte Bestellung';
      this.querySelector('[data-summary-items]').hidden = data.scope !== 'partial';
      this.showStep('review');
      this.focusStep(this.review.querySelector('h2'));
    }
    async submitWithdrawal() {
      if (!this.submission || !this.endpoint || this.busy || this.receipt) return;
      this.clearError();
      this.busy = true;
      this.setAttribute('data-busy', '');
      this.confirm.disabled = true;
      this.edit.disabled = true;
      this.review.setAttribute('aria-busy', 'true');
      this.status.textContent = 'Dein Widerruf wird übermittelt …';
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 35000);
      try {
        const response = await fetch(this.endpoint, {
          method:'POST', headers:{'Content-Type':'application/json'},
          credentials:'omit', cache:'no-store', referrerPolicy:'no-referrer',
          body:JSON.stringify(this.submission), signal:controller.signal
        });
        const result = await response.json().catch(() => null);
        const r = result?.receipt;
        const validReceipt = r && r.id === this.submission.requestId &&
          typeof r.receivedAt === 'string' && Number.isFinite(Date.parse(r.receivedAt)) &&
          ['name','email','order','scope','items'].every(key => typeof r[key] === 'string' && r[key] === this.submission[key]);
        if (!response.ok || result?.accepted !== true || !validReceipt || !['sent','pending'].includes(result.confirmation)) {
          const error = new Error('submission');
          error.httpStatus = response.status;
          throw error;
        }
        this.receipt = r;
        this.querySelector('[data-receipt-id]').textContent = r.id;
        this.querySelector('[data-receipt-date]').textContent = this.formatDate(r.receivedAt);
        this.querySelector('[data-confirmation-message]').textContent = result.confirmation === 'sent'
          ? `Wir haben eine Eingangsbestätigung an ${r.email} versandt. Bitte prüfe gegebenenfalls auch deinen Spam-Ordner.`
          : `Deine Erklärung wurde gespeichert. Die E-Mail-Bestätigung an ${r.email} verzögert sich und wird erneut zugestellt. Du musst deinen Widerruf nicht erneut senden. Du kannst die Bestätigung bereits herunterladen.`;
        this.status.textContent = '';
        this.showStep('receipt');
        this.focusStep(this.receiptPanel.querySelector('h2'));
        this.fields.disabled = true;
      } catch (error) {
        this.error.textContent = error.httpStatus === 429
          ? `Es gab zu viele Übermittlungsversuche. Bitte versuche es später noch einmal oder sende deinen Widerruf per E-Mail an ${this.dataset.support}. Eine Annahme dieser Übermittlung konnte nicht bestätigt werden.`
          : `Wir konnten die Übermittlung nicht bestätigen. Bitte versuche es mit denselben Angaben erneut oder sende deinen Widerruf per E-Mail an ${this.dataset.support}. Wenn du bereits eine Eingangsbestätigung erhalten hast, ist kein erneuter Versand erforderlich.`;
        this.error.hidden = false;
        this.status.textContent = '';
      } finally {
        clearTimeout(timer);
        this.busy = false;
        this.removeAttribute('data-busy');
        this.review.removeAttribute('aria-busy');
        this.confirm.disabled = Boolean(this.receipt);
        this.edit.disabled = Boolean(this.receipt);
      }
    }
    formatDate(value) {
      return new Intl.DateTimeFormat('de-DE', {dateStyle:'long',timeStyle:'long',timeZone:'Europe/Berlin'}).format(new Date(value));
    }
    downloadReceipt() {
      if (!this.receipt) return;
      const r = this.receipt;
      const content = [
        'SMARTJACKET – EINGANGSBESTÄTIGUNG DES WIDERRUFS', '',
        'Empfänger: VARENIQ Onlinehandel UG (haftungsbeschränkt)',
        'Von-Leerodt-Str. 7, 52445 Titz, Deutschland',
        'E-Mail: support@mysmartjacket.eu', '',
        `Belegnummer: ${r.id}`, `Eingang: ${this.formatDate(r.receivedAt)}`,
        `Serverzeit (UTC): ${r.receivedAt}`, '',
        `Name: ${r.name}`, `E-Mail für die Eingangsbestätigung: ${r.email}`,
        `Bestellung: ${r.order}`, '', 'Deine Erklärung:', this.declaration(r), '',
        'Diese Bestätigung dokumentiert den Eingang der Widerrufserklärung. Sie ist keine Bestätigung einer bereits erfolgten Rückzahlung.',
        'Informationen zu Rücksendung und Erstattung: https://mysmartjacket.eu/policies/refund-policy', ''
      ].join('\n');
      const url = URL.createObjectURL(new Blob(['\uFEFF', content], {type:'text/plain;charset=utf-8'}));
      const a = document.createElement('a');
      a.href = url;
      a.download = `SmartJacket-Widerruf-${String(r.id).replace(/[^a-zA-Z0-9-]/g, '').slice(0,64)}.txt`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
  }
  customElements.define('smartjacket-withdrawal', SmartJacketWithdrawal);
})();
